import { TranscricaoListaItem } from '../../../../interfaces/entities/Transcricao/TranscricaoListaItem';
import { TranscricaoDetalhe } from '../../../../interfaces/entities/Transcricao/TranscricaoDetalhe';
import { getTranscricaoStatusConfig } from '../transcricaoStatusUtils';

const pickString = (item: TranscricaoListaItem, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = item[key];
    if (value !== null && value !== undefined) {
      const text = String(value).trim();
      if (text) return text;
    }
  }
  return undefined;
};

export const getTranscricaoItemId = (item: TranscricaoListaItem): string | undefined => {
  const raw = item.idTranscricao ?? item.id;
  if (raw === null || raw === undefined) return undefined;
  const text = String(raw).trim();
  return text || undefined;
};

export const getTranscricaoProtocolo = (item: TranscricaoListaItem): string =>
  pickString(
    item,
    'codProtocoloans',
    'codProtocoloAns',
    'CodProtocoloans',
    'protocolo',
    'numeroProtocolo',
  ) ?? '—';

export const getTranscricaoDataSolicitacao = (item: TranscricaoListaItem): string | undefined =>
  pickString(
    item,
    'dataCadastro',
    'DataCadastro',
    'dataSolicitacao',
    'data',
    'dtSolicitacao',
  );

/** Retorna timestamp para ordenação; datas inválidas/ausentes ficam no final (0). */
export const getTranscricaoDataSolicitacaoTimestamp = (item: TranscricaoListaItem): number => {
  const raw = getTranscricaoDataSolicitacao(item);
  if (!raw) return 0;

  const trimmed = raw.trim();

  if (trimmed.includes('/')) {
    const [datePart, timePart] = trimmed.split(/\s+/);
    const [dia, mes, ano] = (datePart ?? '').split('/').map((part) => Number(part));
    if (!dia || !mes || !ano) return 0;

    const [hora = 0, minuto = 0, segundo = 0] = (timePart ?? '')
      .split(':')
      .map((part) => Number(part) || 0);

    const parsed = new Date(ano, mes - 1, dia, hora, minuto, segundo);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

export const getTranscricaoCodStatus = (item: TranscricaoListaItem): string | undefined =>
  pickString(item, 'codStatusTranscricao', 'CodStatusTranscricao');

export const getTranscricaoDescrStatus = (item: TranscricaoListaItem): string | undefined =>
  pickString(
    item,
    'descrStatusTranscricao',
    'DescrStatusTranscricao',
    'descricaoStatus',
    'status',
    'situacao',
  );

export const getTranscricaoStatusFromItem = (item: TranscricaoListaItem) =>
  getTranscricaoStatusConfig(getTranscricaoCodStatus(item), getTranscricaoDescrStatus(item));

export const formatTranscricaoDataHora = (
  raw?: string,
): { data: string; hora: string } => {
  if (!raw) return { data: '—', hora: '' };

  const trimmed = raw.trim();

  if (trimmed.includes('/')) {
    const [datePart, timePart] = trimmed.split(/\s+/);
    return {
      data: datePart ?? '—',
      hora: timePart?.substring(0, 5) ?? '',
    };
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const dia = String(parsed.getDate()).padStart(2, '0');
    const mes = String(parsed.getMonth() + 1).padStart(2, '0');
    const ano = parsed.getFullYear();
    const hora = String(parsed.getHours()).padStart(2, '0');
    const minuto = String(parsed.getMinutes()).padStart(2, '0');
    return { data: `${dia}/${mes}/${ano}`, hora: `${hora}:${minuto}` };
  }

  return { data: trimmed, hora: '' };
};

export const formatTranscricaoDataHoraLinha = (raw?: string): string => {
  if (!raw) return '—';

  const trimmed = raw.trim();

  if (trimmed.includes('/')) {
    const [datePart, ...timeParts] = trimmed.split(/\s+/);
    const timePart = timeParts.join(' ').trim();
    return timePart ? `${datePart} às ${timePart}` : (datePart ?? '—');
  }

  const { data, hora } = formatTranscricaoDataHora(raw);
  return hora ? `${data} às ${hora}` : data;
};

export const mapListaItemToDetalheFallback = (
  item: TranscricaoListaItem,
): TranscricaoDetalhe => {
  const protocolo = getTranscricaoProtocolo(item);

  return {
    idTranscricao: getTranscricaoItemId(item),
    codProtocoloans: protocolo !== '—' ? protocolo : undefined,
    dataCadastro: getTranscricaoDataSolicitacao(item),
    codStatusTranscricao: getTranscricaoCodStatus(item),
    descrStatusTranscricao: getTranscricaoDescrStatus(item),
    nomeBeneficiario: pickString(item, 'nomeBeneficiario', 'NomeBeneficiario', 'beneficiario'),
    codTipoTranscricao: pickString(item, 'codTipoTranscricao', 'CodTipoTranscricao'),
    dataAceite: pickString(item, 'dataAceite', 'DataAceite'),
  };
};

export const mergeDetalheComListaItem = (
  detalhe: TranscricaoDetalhe,
  listaItem?: TranscricaoListaItem | null,
): TranscricaoDetalhe => {
  if (!listaItem) return detalhe;

  const fallback = mapListaItemToDetalheFallback(listaItem);

  return {
    ...fallback,
    ...detalhe,
    idTranscricao: detalhe.idTranscricao ?? fallback.idTranscricao,
    codProtocoloans: detalhe.codProtocoloans ?? fallback.codProtocoloans,
    dataCadastro: detalhe.dataCadastro ?? fallback.dataCadastro,
    dataAceite: detalhe.dataAceite ?? fallback.dataAceite,
    codTipoTranscricao: detalhe.codTipoTranscricao ?? fallback.codTipoTranscricao,
    codStatusTranscricao: detalhe.codStatusTranscricao ?? fallback.codStatusTranscricao,
    descrStatusTranscricao: detalhe.descrStatusTranscricao ?? fallback.descrStatusTranscricao,
    nomeBeneficiario: detalhe.nomeBeneficiario ?? fallback.nomeBeneficiario,
  };
};
